import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { Investor } from "../../../../../api/types/fundStructureTypes";
import type { FieldIdToValueMap, ObjectClassDefinition } from "../../../../../api/types/objectTypes";
import FieldValuesSection from "../../../../entityFields/FieldValuesSection";

interface Props {
  investor: Investor;
  objectDefinition: ObjectClassDefinition;
  onInvestorDetailsChange: (details: Partial<Investor>) => void;
  hasPermissionsToManageFields: boolean;
}

const updateFieldValues = withErrorHandling(adminApi.updateInvestorFieldValues);

const InvestorCard = ({ investor, objectDefinition, onInvestorDetailsChange, hasPermissionsToManageFields }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [renderKey, setRenderKey] = useState(0);

  const getFieldValues = useCallback(() => adminApi.getInvestorFieldValues(investor.id), [investor.id]);

  const [fieldValuesResp, fieldValuesError, { setData: setFieldValues, isFetching: isFetchingFieldValues }] = useFetch(
    getFieldValues,
    { onSetData: () => setRenderKey((prev) => prev + 1) }
  );

  const getCardLayout = useCallback(
    () => adminApi.getCardLayout(objectDefinition.objectType),
    [objectDefinition.objectType]
  );

  const [cardLayoutResp, cardLayoutError, { isFetching: isFetchingCardLayout }] = useFetch(getCardLayout, {
    onSetData: () => setRenderKey((prev) => prev + 1),
  });

  const saveFieldValues = async (fieldValues: FieldIdToValueMap) => {
    const values = Object.entries(fieldValues).map(([fieldId, value]) => ({ fieldId, value }));
    const [resp, error] = await updateFieldValues(investor.id, { values });
    if (error) {
      sendNotificationError(error.message);
      logError(error, `[InvestorDetailsSection] saveFieldValues`);
      return false;
    }

    sendNotification("Changes saved");
    setFieldValues(resp);
    setRenderKey((prev) => prev + 1);

    const nameFieldValue = resp.values.find(({ fieldId }) => fieldId.toLowerCase() === "name");
    const title = nameFieldValue?.value?.toString() || investor.title;
    onInvestorDetailsChange({
      title,
      updateAt: new Date().toISOString(),
    });

    return true;
  };

  if (fieldValuesError || cardLayoutError) {
    logError(fieldValuesError || cardLayoutError, "[InvestorDetailsSection]");
    return <DataLoadingFailed title="Loading investor details" />;
  }

  const fieldValues = fieldValuesResp
    ? Object.fromEntries(fieldValuesResp.values.map(({ fieldId, value }) => [fieldId, value]))
    : {};

  const cardLayout = cardLayoutResp ?? { objectType: objectDefinition.objectType, fieldGroups: [] };

  return (
    <FieldValuesSection
      key={renderKey.toString()}
      objectType={objectDefinition.objectType}
      objectId={investor.id}
      businessCentralType={objectDefinition.businessCentralType}
      fields={objectDefinition.fields}
      cardLayout={cardLayout}
      fieldValues={fieldValues}
      lookupObjects={fieldValuesResp?.lookupObjects}
      allowEditing={hasPermissionsToManageFields}
      saveFieldValues={saveFieldValues}
      isLoading={isFetchingFieldValues || !fieldValuesResp || isFetchingCardLayout || !cardLayoutResp}
    />
  );
};

export default InvestorCard;
